<template>
  <AppLayout :title="pageTitle" back-to="clients">
    <div v-if="!data" class="text-muted">Carregando...</div>

    <div v-else>
      <div class="row mb-2">
        <div class="col-12">
          <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
            <span class="badge" :class="data.client.active ? 'text-bg-success' : 'text-bg-secondary'">
              {{ data.client.active ? 'Ativo' : 'Inativo' }}
            </span>
            <span class="badge text-bg-light border">Usuário: {{ data.client.username }}</span>
            <span class="badge text-bg-light border">Plano: {{ data.client.plan?.name ?? 'Sem plano' }}</span>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-primary-subtle text-primary"><i class="bi bi-cash-coin"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Vendas hoje</h6>
              <h3 class="mb-0">{{ formatter.currency(data.sales_today.total) }}</h3>
              <small class="text-muted">{{ data.sales_today.count }} venda(s)</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-success-subtle text-success"><i class="bi bi-graph-up-arrow"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Vendas no mês</h6>
              <h3 class="mb-0">{{ formatter.currency(data.sales_month_total) }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-warning-subtle text-warning"><i class="bi bi-box-seam-fill"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Produtos cadastrados</h6>
              <h3 class="mb-0">{{ data.products_count }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-info-subtle text-info"><i class="bi bi-safe2"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Caixas abertos agora</h6>
              <h3 class="mb-0">{{ data.open_cashiers_count }}</h3>
              <small class="text-muted">{{ data.operators_count }} operador(es) cadastrado(s)</small>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <h2 class="section-title">Vendas do mês (dia 1 até hoje)</h2>
          <div class="card">
            <div class="card-body">
              <canvas ref="salesChartCanvas" height="90"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <h2 class="section-title">Consumo do plano</h2>
          <div class="card">
            <div class="card-body">
              <div v-for="(item, resource) in usageLabels" :key="resource" class="usage-item">
                <div class="d-flex justify-content-between">
                  <span>{{ item }}</span>
                  <span class="text-muted">
                    {{ data.usage.registrations[resource].count }}{{ data.usage.registrations[resource].limit ? ' / ' + data.usage.registrations[resource].limit : ' (sem limite)' }}
                  </span>
                </div>
                <div class="progress" style="height: 6px" v-if="data.usage.registrations[resource].percent !== null">
                  <div
                    class="progress-bar"
                    :class="data.usage.registrations[resource].percent >= 100 ? 'bg-danger' : (data.usage.registrations[resource].percent >= 80 ? 'bg-warning' : 'bg-success')"
                    :style="{ width: data.usage.registrations[resource].percent + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-6 mb-3">
          <h2 class="section-title">Atividade</h2>
          <div class="card">
            <div class="card-body">
              <dl class="row mb-0">
                <dt class="col-7">Dias com venda (30d)</dt><dd class="col-5">{{ data.usage.activity.active_days_30d }}</dd>
                <dt class="col-7">Operadores ativos (30d)</dt>
                <dd class="col-5">{{ data.usage.activity.active_operators_30d }} / {{ data.usage.activity.operators_total }}</dd>
                <dt class="col-7">Última venda</dt><dd class="col-5">{{ formatter.dateTime(data.usage.activity.last_sale_at) ?? '—' }}</dd>
                <dt class="col-7">Último acesso do dono</dt><dd class="col-5">{{ formatter.dateTime(data.usage.activity.last_login_at) ?? 'nunca' }}</dd>
              </dl>
              <span class="badge mt-1" :class="activityBadgeClass">{{ activityBadgeLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <h2 class="section-title">Módulos</h2>
          <div class="card">
            <div class="card-body">
              <div class="form-check form-switch">
                <input
                  v-model="servicesEnabled"
                  @change="toggleServices"
                  :disabled="salvandoServicos"
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="inp_services_enabled"
                >
                <label class="form-check-label" for="inp_services_enabled">Serviços + Agenda</label>
              </div>
              <p class="text-muted small mt-2 mb-0">
                Libera, na loja desse cliente, o cadastro de serviços, orçamento (que pode virar venda) e agenda de horários.
                Ele decide, nas permissões de cada operador, quem vende serviço e quem emite orçamento.
              </p>
              <div v-if="erroServicos" class="text-danger small mt-2">{{ erroServicos }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <h2 class="section-title">Dados do dono da loja</h2>
          <div class="card">
            <div class="card-body">
              <dl class="row mb-0">
                <dt class="col-4">Nome</dt><dd class="col-8">{{ data.client.name }}</dd>
                <dt class="col-4">Loja</dt><dd class="col-8">{{ data.client.name_business ?? '—' }}</dd>
                <dt class="col-4">E-mail</dt><dd class="col-8">{{ data.client.email }}</dd>
                <dt class="col-4">Telefone</dt><dd class="col-8">{{ data.client.phone ?? '—' }}</dd>
                <dt class="col-4">Cadastro</dt><dd class="col-8">{{ formatter.date(data.client.created_at) }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Chart from 'chart.js/auto';
import AppLayout from "@/components/AppLayout.vue";
import { clients } from "@/models/clients";
import formatter from "@/services/formatter";

export default {
  name: 'ClientDetailView',
  components: { AppLayout },
  setup() {
    const route = useRoute();
    const Clients = clients();
    const data = ref(null);
    const salesChartCanvas = ref(null);
    let salesChart = null;
    const servicesEnabled = ref(false);
    const salvandoServicos = ref(false);
    const erroServicos = ref(null);

    const pageTitle = computed(() => data.value?.client?.name_business || data.value?.client?.name || 'Detalhes do Cliente');

    const usageLabels = {
      products: 'Produtos',
      operators: 'Operadores',
      stores: 'Lojas',
    };

    // Sem nenhum sinal de atividade ainda (cliente novo) não é a mesma
    // coisa que sumido — só classifica quem já teve alguma atividade.
    const daysSinceLastActivity = computed(() => {
      const lastActivityAt = data.value?.usage?.activity?.last_activity_at;
      if (!lastActivityAt) return null;
      return Math.floor((Date.now() - new Date(lastActivityAt).getTime()) / 86400000);
    });

    const activityBadgeClass = computed(() => {
      const days = daysSinceLastActivity.value;
      if (days === null) return 'text-bg-secondary';
      if (days > 14) return 'text-bg-danger';
      if (days > 7) return 'text-bg-warning';
      return 'text-bg-success';
    });

    const activityBadgeLabel = computed(() => {
      const days = daysSinceLastActivity.value;
      if (days === null) return 'Sem atividade registrada';
      if (days === 0) return 'Ativo hoje';
      return `Última atividade há ${days} dia(s)`;
    });

    function dayLabel(isoDate) {
      const [, month, day] = isoDate.split('-');
      return `${day}/${month}`;
    }

    function renderSalesChart(salesByDay) {
      if (!salesChartCanvas.value) return;

      const labels = salesByDay.map(day => dayLabel(day.date));
      const totals = salesByDay.map(day => day.total);

      salesChart = new Chart(salesChartCanvas.value, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Vendas',
            data: totals,
            borderColor: '#6f42c1',
            backgroundColor: 'rgba(111, 66, 193, 0.15)',
            tension: 0.2,
            fill: true,
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (context) => formatter.currency(context.parsed.y) } },
          },
          scales: {
            y: { beginAtZero: true, ticks: { callback: (value) => formatter.currency(value) } },
          },
        },
      });
    }

    // Só o admin liga/desliga esse módulo por cliente (o dono da loja só
    // vê o status em Configurações) — ver AdminClientController::update.
    async function toggleServices() {
      const desejado = servicesEnabled.value;
      erroServicos.value = null;
      salvandoServicos.value = true;
      try {
        await Clients.update(route.params.id, { services_enabled: desejado });
      } catch (error) {
        servicesEnabled.value = !desejado;
        erroServicos.value = firstErrorMessage(error);
      } finally {
        salvandoServicos.value = false;
      }
    }

    function firstErrorMessage(err) {
      const firstError = Object.values(err?.errors ?? {})[0];
      return firstError?.[0] ?? err?.message ?? 'Não foi possível salvar. Tente novamente.';
    }

    onMounted(async () => {
      data.value = await Clients.stats(route.params.id);
      servicesEnabled.value = !!data.value?.client?.services_enabled;
      if (data.value?.sales_by_day) {
        await nextTick();
        renderSalesChart(data.value.sales_by_day);
      }
    });

    onBeforeUnmount(() => {
      salesChart?.destroy();
    });

    return {
      data, formatter, salesChartCanvas, pageTitle, usageLabels, activityBadgeClass, activityBadgeLabel,
      servicesEnabled, salvandoServicos, erroServicos, toggleServices,
    };
  },
}
</script>
<style scoped>
.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 0.75rem;
}

.stat-card__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
}

.usage-item + .usage-item {
  margin-top: 0.75rem;
}
</style>
